/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { useCallback } from 'react';

import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager';

import type { AdfStats } from './useAdfStats';

const STATSIG_DYNAMIC_CONFIG = 'cc_complexit_page_id_allowlist';

type UseProgressiveRenderingConfigResponseType = {
	isComplexPage: boolean;
	batchSize: number;
};

type UseProgressiveRenderingConfigProps = {
	totalNodeCount: AdfStats['totalNodeCount'];
};

const DEFAULT_COMPLEX_THREHOLD = 10000;
const DEFAULT_BATCH_SIZE = 1000;
const DEFAULT_MULTIPLIER = 10;

// complexitNodes value example: conplexitNodes=1000,10 where 1000 is the batch size and 10 is the multiplier
const useQueryParamComplexitNodes = createSingleQueryParamHook('complexitNodes');

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
const ProgressiveRenderingConfigQuery = gql`
	query ProgressiveRenderingConfigQuery {
		user: confluence_atlassianUser(current: true) {
			id
			emails {
				value
			}
		}
	}
`;

export const useProgressiveRenderingConfig = ({
	totalNodeCount,
}: UseProgressiveRenderingConfigProps): UseProgressiveRenderingConfigResponseType => {
	const [contentId] = usePageContentId();
	const pageIdAllowList = StatsigConfigurations.getStringList(STATSIG_DYNAMIC_CONFIG, []);
	const isContentIdEnabled = !!contentId && pageIdAllowList.includes(contentId);

	const complexitNodesConfig = useQueryParamComplexitNodes()?.split(',');

	const isComplexPage = useCallback(
		(threshold: number) => {
			return (
				fg('cc_complexit_fe_progressive_rendering_condition') &&
				(isContentIdEnabled || totalNodeCount >= threshold)
			);
		},
		[isContentIdEnabled, totalNodeCount],
	);

	const { data, loading, error } = useQuery(ProgressiveRenderingConfigQuery, {
		skip:
			!fg('cc_complexit_fe_progressive_adf_rendering_v2') ||
			!Boolean(complexitNodesConfig) ||
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR),
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const isAtlassianUser =
		Boolean(data?.user?.emails.find((email: any) => email.value.match(/@atlassian.com$/g))) ??
		false;

	// Early return if query params are not enabled
	if (loading || error || !isAtlassianUser) {
		return {
			isComplexPage: isComplexPage(DEFAULT_COMPLEX_THREHOLD),
			batchSize: DEFAULT_BATCH_SIZE,
		};
	}

	// Support partial value format like ,10 or 1000,
	const complexitBatchSize = Number(complexitNodesConfig?.[0] || DEFAULT_BATCH_SIZE);
	const complexitNodes =
		complexitBatchSize * Number(complexitNodesConfig?.[1] || DEFAULT_MULTIPLIER);
	const complexityThreshold = isNaN(complexitNodes) ? DEFAULT_COMPLEX_THREHOLD : complexitNodes;

	return {
		isComplexPage: isComplexPage(complexityThreshold),
		batchSize: complexitBatchSize,
	};
};
